import { DatePicker, KpiCard } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets/outletSelectors';
import { Download, Receipt, RefreshCw } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import {
  ExpensesReportRow,
  fetchExpensesReport,
} from '../../../../services/accountingReportingService';
import { apiError } from '../../../../util/notify';
import { exportToCsv } from '../../accommodation/reports/reportUtils';
import { formatCurrencyValue } from './accountingReports';

export const ExpensesReport: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState<ExpensesReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const data = await fetchExpensesReport(
        companyId,
        outletId,
        dateFrom,
        dateTo,
      );
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to load expenses report',
      );
    } finally {
      setIsLoading(false);
    }
  }, [companyId, outletId, dateFrom, dateTo]);

  const totalExpenses = rows.reduce((s, r) => s + r.net_amount, 0);

  const handleExport = () => {
    exportToCsv(
      'expenses-report',
      rows.map((r) => ({
        'Account Code': r.account_code,
        'Account Name': r.account_name,
        Subtype: r.account_subtype ?? '',
        'Total Debits': formatCurrencyValue(r.total_debits),
        'Total Credits': formatCurrencyValue(r.total_credits),
        'Net Amount': formatCurrencyValue(r.net_amount),
      })),
    );
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <DatePicker
          mode="single"
          id="exp-date-from"
          label="From"
          value={dateFrom}
          onChange={setDateFrom}
        />
        <DatePicker
          mode="single"
          id="exp-date-to"
          label="To"
          value={dateTo}
          onChange={setDateTo}
        />
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Loading…' : 'Run report'}
        </button>
        {loaded && rows.length > 0 && (
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {loaded && (
        <>
          {/* KPIs */}
          <div className="grid gap-4 sm:grid-cols-2">
            <KpiCard
              title="Total Expenses"
              value={formatCurrencyValue(totalExpenses)}
              sub={`${rows.length} expense account${rows.length !== 1 ? 's' : ''}`}
              icon={Receipt}
              accent="bg-red-500"
            />
            <KpiCard
              title="Expense Accounts"
              value={rows.length}
              sub="with activity in period"
              icon={Receipt}
              accent="bg-pos-primary"
            />
          </div>

          {/* Table */}
          {rows.length === 0 ? (
            <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
              No expense entries found in this period
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="border-b border-gray-100 px-5 py-3">
                <h4 className="text-sm font-semibold text-gray-900">
                  Expense Accounts ({rows.length})
                </h4>
              </div>
              <div className="max-h-[32rem] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">
                        Code
                      </th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">
                        Account Name
                      </th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">
                        Debits
                      </th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">
                        Credits
                      </th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">
                        Net Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((r) => (
                      <tr key={r.account_code} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-400">
                          {r.account_code}
                        </td>
                        <td className="px-4 py-2.5 text-gray-900">
                          {r.account_name}
                          {r.account_subtype && (
                            <span className="ml-2 text-xs text-gray-400">
                              ({r.account_subtype})
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700">
                          {formatCurrencyValue(r.total_debits)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-green-700">
                          {r.total_credits > 0
                            ? formatCurrencyValue(r.total_credits)
                            : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-900">
                          {formatCurrencyValue(r.net_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-2.5 text-xs font-semibold text-gray-600"
                      >
                        Total Expenses
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold text-gray-900">
                        {formatCurrencyValue(totalExpenses)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to view
          expenses.
        </div>
      )}
    </div>
  );
};
